import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription, filter } from 'rxjs';
import { DataGridColumn, DataGridExportOptions, SummaryCard, DashboardSummary, DashboardMetrics } from 'src/app/utils/interfaces';
import { UserService } from 'src/app/services/user.service';
import { UserInfo, LogStatus, LogError } from '@memberjunction/core';
import { CommitteesEntity } from 'mj_generatedentities';
import { AcsDataGridComponent } from '../shared/acs-data-grid/acs-data-grid';
import { DropdownOption } from '../shared/acs-button/acs-button';
import { Router } from '@angular/router';
import { ChapterGridColumns, GridExportOptions } from 'src/app/utils/grid-constants';
import { DataFetchService } from 'src/app/services/data-fetch.service';
import { EntityNames, QueryNames } from 'src/app/utils/constants';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { ActiveUserService } from 'src/app/services/active-user.service';

@Component({
  selector: 'acs-all-chapters',
  templateUrl: './all-chapters.html',
  styleUrls: ['../../../styles/_page-layout.scss', './all-chapters-specific.scss']
})
export class AllChaptersComponent implements OnInit, OnDestroy {
  @ViewChild('chaptersGrid') chaptersGrid!: AcsDataGridComponent;
  private subscription = new Subscription();
  readonly entityName = EntityNames.Committees;

  isLoading = false; // Server-side grid handles its own loading
  error: string | null = null;
  
  // Summary data
  summary!: DashboardMetrics;

  summaryCards: SummaryCard[] = [];
  currentUser!: UserInfo;
  
  // Grid Configuration
  gridColumns: DataGridColumn[] = [];
  selectedChaptersRecords: CommitteesEntity[] = [];

  // Export dropdown options
  exportOptions = GridExportOptions;

  constructor(
    private userService: UserService, 
    private activeUserService: ActiveUserService,
    private router: Router,
    private dataFetchService: DataFetchService,
    private userAuthService: UserAuthService
  ) {}
  
  ngOnInit(): void {
    this.subscription.add(
      this.userService.getUserComplete$.pipe(filter(complete => complete)).subscribe(() => {
        this.initializeComponent();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  initializeComponent(): void {
    this.currentUser = this.activeUserService.getActiveUser()!;
    this.gridColumns = ChapterGridColumns;
    this.loadSummaryData();
  }
  
  // Modal states
  showAnnouncementModal = false;
  

  onServerDataError(error: any): void {
    LogError('Error loading Chapters data:', undefined, error);
    this.error = 'Failed to load Chapters data. Please try again.';
  }

  onGridSelectionChange(selectedRecords: CommitteesEntity[]): void {
    this.selectedChaptersRecords = selectedRecords;
  }

  onGridRowClick(record: CommitteesEntity): void {
    LogStatus('Viewing Chapter detail:', undefined, record);
    this.router.navigate(['/chapter-details'], { queryParams: { id: record.ID } });
  }

  onGridExport(options: DataGridExportOptions): void {
    LogStatus('Exporting Chapters data:', undefined, options);
  }

  // Bulk Actions
  publishAnnouncement(): void {
    if (this.selectedChaptersRecords.length === 0) {
      alert('Please select Chapter records first.');
      return;
    }

    // Ensure modal is closed first, then open it
    // This forces a proper state reset
    this.showAnnouncementModal = false;

    // Use setTimeout to ensure the modal component has time to reset
    setTimeout(() => {
      this.showAnnouncementModal = true;
    }, 10);
  }

  onExportOptionSelected(option: DropdownOption): void {
    const format = option.value as 'csv' | 'excel' | 'pdf';
    
    // Use the chaptersGrid to perform the export
    if (this.chaptersGrid) {
      this.chaptersGrid.onExport(format);
      const exportMessage = this.selectedChaptersRecords.length > 0 
        ? `Exporting ${this.selectedChaptersRecords.length} selected chapters as ${format.toUpperCase()}`
        : `Exporting all chapters as ${format.toUpperCase()}`;
      LogStatus(exportMessage, undefined, this.selectedChaptersRecords);
    } else {
      alert('Grid not available for export. Please try again.');
    }
  }

  clearSelection(): void {
    this.selectedChaptersRecords = [];
    if (this.chaptersGrid) {
      this.chaptersGrid.clearSelection();
    }
  }

  // Modal Event Handlers
  onAnnouncementModalClosed(): void {
    this.showAnnouncementModal = false;
  }

  onAnnouncementPublished(announcement: any): void {
    LogStatus('Announcement published:', undefined, announcement);
    this.showAnnouncementModal = false;

    // Clear the selection after successful publication
    this.clearSelection();
  }


  private async loadSummaryData(): Promise<void> {
    // Initialize summary data - will be updated when data loads
    if(this.userAuthService.isStaff()) {
      await this.getDashboardMetricsForStaff();
    }
    else {
      await this.getDashboardMetricsForChapterAdmins();
    }

    this.updateSummaryCards();
  }

  private async getDashboardMetricsForStaff(): Promise<void> {
    const result = await this.dataFetchService.executeQuery<DashboardMetrics>({
      QueryName: QueryNames.DashboardMetricsSummaryForStaff,
    });
    if(result.success && result.data.length > 0) {
      this.summary = result.data[0];
    }
    else {
      this.summary = {
        Email: this.currentUser.Email,
        TotalChapters: 0,
        TotalActiveMembers: 0,
        TotalFellows: 0
      }
    }
  }

  private async getDashboardMetricsForChapterAdmins(): Promise<void> {
    const result = await this.dataFetchService.executeQuery<DashboardMetrics>({
      QueryName: QueryNames.DashboardMetricsSummary,
      Parameters: {
        Email: this.currentUser.Email
      }
    });
    if(result.success && result.data.length > 0) {
      this.summary = result.data[0];
    }
    else {
      this.summary = {
        Email: this.currentUser.Email,
        TotalChapters: 0,
        TotalActiveMembers: 0,
        TotalFellows: 0
      }
    }
  }

  private updateSummaryCards(): void {
    this.summaryCards = [
      {
        id: 'total-chapters',
        value: `${this.summary.TotalChapters}`,
        label: this.summary.TotalChapters === 1 ? 'Chapter' : 'Chapters',
        sublabel: 'Total in directory',
        icon: '🏥',
        variant: 'primary',
        format: 'number'
      },
      {
        id: 'active-members',
        value: `${this.summary.TotalActiveMembers}`,
        label: 'Active Members',
        sublabel: 'Across all chapters',
        icon: '👥',
        variant: 'success',
        format: 'number'
      },
      {
        id: 'fellows',
        value: `${this.summary.TotalFellows}`,
        label: 'Fellows',
        sublabel: 'Total Fellows',
        icon: '👥',
        variant: 'info',
        format: 'number'
      }
    ];
  }


  get userRole(): string | null {
    // Get role from UserAuthService which fetches from API
    return this.userAuthService.getUserRole();
  }
}